import { Fragment } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { NativeAdCard } from "@/components/ad-slot";
import { useQuery } from "@tanstack/react-query";
import { Flame, ArrowUpRight, Users, TrendingUp, Layers } from "lucide-react";
import { BoardOpeningBanner } from "@/components/board-opening-banner";
import { LiveStatsPill } from "@/components/live-stats-pill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { categoriesQuery, boardsOverviewQuery, hotActivityQuery } from "@/lib/queries";
import { formatUsd, formatHeat, labelForType, BOOST_STEP_CENTS, EMPTY_BOARD_ENTRY_CENTS } from "@/lib/behot";
import type { Category, BoardOverview, Listing } from "@/lib/queries";
import { BOARDS_OG_IMAGE, BOARDS_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";
import { HOT_TOPICS } from "@/lib/hot-cluster";
import { getHotClusterContent } from "@/lib/hot-cluster.i18n";
import { proxiedImage } from "@/lib/remote-image";

export const Route = createFileRoute("/hot/")({
  // Prefetched on the server so every board, its Heat totals and the top
  // holders are present in the initial HTML for crawlers.
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(categoriesQuery),
      context.queryClient.ensureQueryData(boardsOverviewQuery),
      context.queryClient.ensureQueryData(hotActivityQuery),
    ]);
  },
  head: () =>

    buildPageMetadata({
      path: "/hot",
      title: "What's Hot Right Now — Live Rankings | BeHot.Now",
      description:
        "See what's hot right now across creators, artists, AI, startups, apps, music, brands and tech. Explore live BeHot.Now rankings and the biggest movers.",
      keywords: [
        "what's hot right now",
        "live rankings",
        "trending now",
        "top creators",
        "hot listings",
        "real time leaderboard",
        "trending projects",
        "BeHot.Now boards",
      ],
      ogTitle: "What's Hot Right Now — Live Rankings",
      ogDescription:
        "Every live board on BeHot.Now: total Heat, entry price and who holds the top positions today.",
      image: BOARDS_OG_IMAGE,
      imageAlt: BOARDS_OG_IMAGE_ALT,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "What's hot", path: "/hot" },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "What's Hot Right Now",
        url: `${SITE_URL}/hot`,
        description:
          "Live rankings across creators, artists, AI, startups, apps, music, brands and tech.",
      },
    }),
  component: HotPage,
});

const EMPTY: BoardOverview = { heat: 0, listings: 0, boosters: 0, entryFloor: 0, top: [] };

function Holder({ listing, heat, rank }: { listing: Listing; heat: number; rank: number }) {
  return (
    <Link
      to="/p/$slug"
      params={{ slug: listing.slug }}
      className="flex items-center gap-3 rounded-xl border border-border/60 bg-background/60 px-3 py-2 transition-colors hover:border-primary/50"
    >
      <span className="rank-numeral w-6 text-sm">{String(rank).padStart(2, "0")}</span>
      {listing.logo_url ? (
        <img src={proxiedImage(listing.logo_url)} alt="" loading="lazy" className="size-8 rounded-lg object-cover" />
      ) : (
        <span className="flex size-8 items-center justify-center rounded-lg bg-accent text-xs font-bold text-accent-foreground">
          {listing.name.charAt(0).toUpperCase()}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{listing.name}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {listing.headline || labelForType(listing.listing_type)}
        </span>
      </span>
      <span className="flex items-center gap-1 text-sm font-semibold text-primary">
        <Flame className="size-3.5" />
        {formatHeat(heat)}
      </span>
    </Link>
  );
}

function BoardCard({
  category,
  subBoards,
  overview,
  stats,
}: {
  category: Category;
  subBoards: Category[];
  overview: Map<string, BoardOverview>;
  stats: BoardOverview;
}) {
  const { t } = useT();
  // Empty or freshly refreshed boards open at 3 Heat; live boards start at 5 Heat.
  const entry =
    stats.listings === 0
      ? EMPTY_BOARD_ENTRY_CENTS
      : Math.max(BOOST_STEP_CENTS, category.opening_boost_cents);


  return (
    <div className="card-surface flex min-w-0 flex-col p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/c/$slug"
            params={{ slug: category.slug }}
            className="font-display text-lg font-bold hover:text-primary"
          >
            {category.name}
          </Link>
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
            {category.description ?? t("boards.card.defaultDescription")}
          </p>
        </div>
        <Badge variant="secondary" className="shrink-0 gap-1">
          <Flame className="size-3 text-primary" />
          {formatHeat(stats.heat)}
        </Badge>
      </div>

      <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-muted/50 py-2">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("boards.card.listings")}</dt>
          <dd className="font-display text-base font-bold">{stats.listings}</dd>
        </div>
        <div className="rounded-lg bg-muted/50 py-2">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("boards.card.boosters")}</dt>
          <dd className="font-display text-base font-bold">{stats.boosters}</dd>
        </div>
        <div className="rounded-lg bg-muted/50 py-2">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("boards.card.entryFrom")}</dt>
          <dd className="font-display text-base font-bold text-primary">{formatUsd(entry)}</dd>
        </div>
      </dl>

      <div className="mt-4 space-y-2">
        {stats.top.length > 0 ? (
          stats.top.map((t2, i) => (
            <Holder key={t2.listing.id} listing={t2.listing} heat={t2.heat} rank={i + 1} />
          ))
        ) : (
          <div className="space-y-2">
            <p className="rounded-xl border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
              {t("boards.card.noHolder")}
            </p>
            <BoardOpeningBanner category={category} isEmpty compact />
          </div>
        )}
      </div>

      {subBoards.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {subBoards.slice(0, 6).map((child) => {
            const cs = overview.get(child.id);
            return (
              <Link
                key={child.id}
                to="/c/$slug"
                params={{ slug: child.slug }}
                className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
              >
                {child.name}
                {cs ? <span className="ml-1 text-primary">{formatHeat(cs.heat)}</span> : null}
              </Link>
            );
          })}
        </div>
      )}

      <div className="mt-auto flex gap-2 pt-4">
        <Button asChild variant="hot" size="sm" className="flex-1">
          <Link to="/get-hot" search={{ category: category.slug, listing: undefined }}>
            {t("boards.card.getOnBoard")}
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to="/c/$slug" params={{ slug: category.slug }}>
            {t("boards.card.view")} <ArrowUpRight className="size-3.5" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

function HotPage() {
  const { t, locale } = useT();
  const { data: categories = [] } = useQuery(categoriesQuery);
  const { data: overview = new Map<string, BoardOverview>() } = useQuery(boardsOverviewQuery);
  const { data: activity = [] } = useQuery(hotActivityQuery);

  const parents = categories.filter((c) => !c.parent_id);
  const statsFor = (parent: Category): BoardOverview => {
    const kids = categories.filter((c) => c.parent_id === parent.id);
    const parts = [parent, ...kids]
      .map((c) => overview.get(c.id))
      .filter((v): v is BoardOverview => Boolean(v));
    if (!parts.length) return EMPTY;
    const top = parts
      .flatMap((p) => p.top)
      .sort((a, b) => b.heat - a.heat)
      .slice(0, 3);
    return {
      heat: parts.reduce((s, p) => s + p.heat, 0),
      listings: parts.reduce((s, p) => s + p.listings, 0),
      boosters: parts.reduce((s, p) => s + p.boosters, 0),
      entryFloor: Math.min(...parts.map((p) => p.entryFloor)),
      top,
    };
  };

  const ranked = parents
    .map((c) => ({ category: c, stats: statsFor(c) }))
    .sort((a, b) => b.stats.heat - a.stats.heat);

  const totalHeat = ranked.reduce((s, r) => s + r.stats.heat, 0);
  const totalListings = ranked.reduce((s, r) => s + r.stats.listings, 0);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <h1 className="font-display text-3xl font-bold sm:text-4xl">
        {t("boards.hot.titlePrefix")} <span className="text-gradient-hot">{t("boards.hot.titleHighlight")}</span>
      </h1>
      <p className="mt-2 max-w-xl text-muted-foreground">
        {t("boards.hot.subtitle")}
      </p>
      <div className="mt-4">
        <LiveStatsPill />
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <div className="card-surface flex items-center gap-3 p-4">
          <Flame className="size-5 text-primary" />
          <div>
            <div className="font-display text-xl font-bold">{formatHeat(totalHeat)}</div>
            <div className="text-xs text-muted-foreground">{t("boards.hot.stat.activeHeat")}</div>
          </div>
        </div>
        <div className="card-surface flex items-center gap-3 p-4">
          <Users className="size-5 text-primary" />
          <div>
            <div className="font-display text-xl font-bold">{totalListings}</div>
            <div className="text-xs text-muted-foreground">{t("boards.hot.stat.listingsHolding")}</div>
          </div>
        </div>
        <Link
          to="/categories"
          className="card-surface flex items-center gap-3 p-4 transition-colors hover:border-primary/50"
        >
          <Layers className="size-5 text-primary" />
          <div>
            <div className="font-display text-xl font-bold">{categories.length}</div>
            <div className="text-xs text-muted-foreground">{t("boards.hot.stat.boardsAndSubboards")}</div>
          </div>
          <ArrowUpRight className="ml-auto size-4 text-muted-foreground" />
        </Link>

      </div>

      {activity.length > 0 && (
        <div className="card-surface mt-4 flex items-center gap-3 overflow-hidden py-3 pl-4">
          <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-primary">
            <TrendingUp className="size-3.5" /> {t("boards.hot.latest")}
          </span>
          <div className="marquee-mask group relative min-w-0 flex-1 overflow-hidden">
            <div className="marquee-track gap-6 pr-6 text-sm group-hover:[animation-play-state:paused]">
              {[0, 1].map((copy) => (
                <div key={copy} className="flex shrink-0 items-center gap-6 pr-6" aria-hidden={copy === 1}>
                  {activity.slice(0, 10).map((a) => (
                    <span key={a.id} className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                      <span className="size-1.5 rounded-full bg-primary/70" />
                      <span className="font-semibold text-foreground">
                        {a.listing?.name ?? t("boards.hot.aListing")}
                      </span>
                      <span className="font-semibold text-primary">
                        +{formatHeat(Number(a.value_cents))}
                      </span>
                      {a.category ? (
                        <span className="text-muted-foreground">{a.category.name}</span>
                      ) : null}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      <div className="mt-8 grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[repeat(2,minmax(0,1fr))]">
        {ranked.map(({ category, stats }, i) => (
          <Fragment key={category.id}>
          {i === 2 && <NativeAdCard slot="hotFeed" className="lg:col-span-2" />}
          <BoardCard
            category={category}
            subBoards={categories.filter((c) => c.parent_id === category.id)}
            overview={overview}
            stats={stats}
          />
          </Fragment>
        ))}
      </div>
    
      <section className="mt-12">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
          {t("hotc.exploreHot")}
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {HOT_TOPICS.map((topic) => (
            <Link
              key={topic.slug}
              to="/hot/$topic"
              params={{ topic: topic.slug }}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              {getHotClusterContent(locale, topic).h1}
            </Link>
          ))}
          <Link
            to="/compare/hot"
            className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {t("hotc.compareOthers")}
          </Link>
        </div>
      </section>
    </div>
  );
}
